import { Button, Grid, Hidden, Icon } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Spin from 'components/Spin';
import moment from 'moment';
import React from 'react';
import { DateRangePicker } from 'react-dates';
import { connect } from 'react-redux';
import Slider from 'react-slick';
import { loadFilterByMonth, loadMyExtract } from 'store/Orbita/Financial/actions';
import financialService from 'v2/services/financial';

import type { FilterBy } from '../..';
import type { DispatchProp } from 'react-redux';
import type { Settings } from 'react-slick';

const styles = withStyles((theme) => ({
  button: {
    margin: `${theme.spacing(1)}px 0`,
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  iconSmall: {
    fontSize: 20,
  },
  carousel: {
    margin: '0 auto',
    maxWidth: 'calc(100vw - 429px)',
    ['@media screen and (max-width: 1366px)']: {
      maxWidth: 'calc(100vw - 360px)',
    },
    ['@media screen and (max-width: 993px)']: {
      maxWidth: 'calc(100vw - 113px)',
      margin: '0 40px',
    },
  },
  carousel_item: {
    backgroundColor: '#586375',
    color: '#fff',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': {
      cursor: 'pointer',
      transition: '0.4s',
      backgroundColor: '#4f5869',
    },
  },
  carousel_item_disabled: {
    backgroundColor: '#586375',
    color: '#fff',
    padding: '20px',
    textAlign: 'center',
    '&:hover': {
      cursor: 'not-allowed',
      transition: '0.4s',
    },
  },
  carousel_item_selected: {
    backgroundColor: '#4f5869',
    color: '#fff',
    padding: '20px',
    textAlign: 'center',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      width: 0,
      height: 0,
      border: '20px solid transparent',
      borderTopColor: '#00aabb',
      borderBottom: 0,
      marginLeft: '-20px',
      marginBottom: '-20px',
    },
  },
  date: {
    color: '#fff',
    marginBottom: 0,
    fontSize: '1.10em',
    fontWeight: 'bold',
  },
  arrow_left: {
    backgroundColor: '#4f5869',
    left: '-40px',
    width: '40px',
    position: 'absolute',
    color: '#fff',
    height: '100%',
    borderRadius: '5px 0 0 5px',
    cursor: 'pointer',
  },
  arrow_right: {
    backgroundColor: '#4f5869',
    right: '-40px',
    width: '40px',
    position: 'absolute',
    color: '#fff',
    top: 0,
    height: '100%',
    borderRadius: '0 5px 5px 0',
    cursor: 'pointer',
  },
  arrow_disabled: {
    backgroundColor: '#586375',
    cursor: 'not-allowed',
  },
  arrow: {
    fontSize: '2.3em',
    position: 'relative',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

interface IMonth {
  date: string;
  valor: number;
}

interface IProps extends DispatchProp<any> {
  monthFilters: IMonth[];
  filterExtract: any;
  classes?: any;
  filterBy: FilterBy;
  onChangeFilter: (type: FilterBy) => void;
}

interface IStates {
  selected: string;
  focusedInput: any;
  startDate: any;
  endDate: any;
}

class Filter extends React.Component<IProps, IStates> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      selected: null,
      focusedInput: null,
      startDate: null,
      endDate: null,
    };
  }

  onClickMonth = async (date: string) => {
    if (date === this.state.selected) {
      return;
    }

    this.setState({ selected: date });

    const startFormattedDate = moment(date).startOf('month').format('YYYY-MM-DD');
    const endFormattedDate = moment(date).endOf('month').format('YYYY-MM-DD');
    const period: { start: string; end: string } = {
      start: startFormattedDate,
      end: endFormattedDate,
    };
    financialService.setExportPeriod(period);

    await this.props.dispatch({ type: 'ADD_FILTER_EXTRACT', filter: { period } });

    await this.props.dispatch(loadMyExtract(1, this.props.filterExtract));
  };

  onChangeDate = async (start: any, end: any) => {
    this.setState({
      startDate: start,
      endDate: end,
    });

    if (start && end) {
      start = moment(start).format('YYYY-MM-DD');
      end = moment(end).format('YYYY-MM-DD');
      await this.props.dispatch({ type: 'ADD_FILTER_EXTRACT', filter: { period: { start, end } } });
      await this.props.dispatch(loadMyExtract(1, this.props.filterExtract));
    }
  };

  componentWillUnmount() {
    this.props.dispatch({ type: 'RESET_FILTER_EXTRACT' });
  }

  async componentDidMount() {
    await this.props.dispatch(loadFilterByMonth());
    if (!!this?.props?.monthFilters.length) {
      const deepCloneMonths: any[] = JSON.parse(JSON.stringify(this.props.monthFilters));

      const monthLastIndex = deepCloneMonths.length - 1;

      const monthReverse = deepCloneMonths.reverse();

      const lastMonthDate = monthReverse[monthLastIndex].date;

      this.onClickMonth(lastMonthDate);

      this.setState({ selected: lastMonthDate });
    }
  }

  render() {
    const { classes, monthFilters, filterBy, onChangeFilter } = this.props;
    const { selected } = this.state;

    const NextArrow = (props: any) => {
      const { onClick } = props;
      return (
        <div className={onClick ? classes.arrow_right : classNames(classes.arrow_right, classes.arrow_disabled)} onClick={onClick}>
          <Icon className={classes.arrow}>arrow_right</Icon>
        </div>
      );
    };

    const PrevArrow = (props: any) => {
      const { onClick } = props;
      return (
        <div className={onClick ? classes.arrow_left : classNames(classes.arrow_left, classes.arrow_disabled)} onClick={onClick}>
          <Icon className={classes.arrow}>arrow_left</Icon>
        </div>
      );
    };

    const settings: Settings = {
      dots: false,
      infinite: false,
      speed: 1000,
      slidesToShow: 8,
      slidesToScroll: 8,
      swipeToSlide: true,
      className: classes.carousel,
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
      responsive: [
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: 5,
            slidesToScroll: 5,
          },
        },
        {
          breakpoint: 1100,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 4,
          },
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
          },
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 2,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ],
    };

    return (
      <>
        <Grid container alignItems='center'>
          <Grid item xs={12} sm={8} md={10}>
            {filterBy === 'Period' && (
              <div style={{ display: 'flex', position: 'relative', alignItems: 'center', justifyContent: 'flex-end' }}>
                <span style={{ marginRight: 12 }}>Período:</span>
                <DateRangePicker
                  startDate={this.state.startDate} // momentPropTypes.momentObj or null,
                  startDateId='your_unique_start_date_id' // PropTypes.string.isRequired,
                  endDate={this.state.endDate} // momentPropTypes.momentObj or null,
                  endDateId='your_unique_end_date_id' // PropTypes.string.isRequired,
                  onDatesChange={({ startDate, endDate }: any) => this.onChangeDate(startDate, endDate)} // PropTypes.func.isRequired,
                  focusedInput={this.state.focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
                  onFocusChange={(focusedInput: any) => this.setState({ focusedInput })} // PropTypes.func.isRequired,
                  displayFormat={'DD/MM/YYYY'}
                  small
                  startDatePlaceholderText={'Data inicial'}
                  endDatePlaceholderText={'Data final'}
                  isOutsideRange={() => false}
                  minimumNights={0}
                  orientation={'horizontal'}
                  numberOfMonths={window.innerWidth <= 760 ? 1 : 2}
                />
              </div>
            )}
          </Grid>

          <Hidden only={['xs']}>
            <Grid item sm={4} md={2}>
              <div style={{ textAlign: 'right' }}>
                <Button id='filter-by-date-button' size='small' onClick={() => onChangeFilter(filterBy)} className={classes.button}>
                  <Icon className={classNames(classes.leftIcon, classes.iconSmall)}>calendar_today</Icon> Filtrar por{' '}
                  {filterBy === 'Period' ? 'mês' : 'período'}
                </Button>
              </div>
            </Grid>
          </Hidden>
        </Grid>
        {filterBy === 'Month' && (
          <>
            {monthFilters && Array.isArray(monthFilters) ? (
              <Slider {...settings}>
                {monthFilters.map((item) => (
                  <div
                    key={item.date}
                    onClick={() => this.onClickMonth(item.date)}
                    className={item.date === selected ? classes.carousel_item_selected : classes.carousel_item}
                  >
                    <p className={classes.date}>{moment(item.date).format('MMMM').toUpperCase()}</p>
                    <p className={classes.date}>{moment(item.date).format('YYYY').toUpperCase()}</p>
                  </div>
                ))}
              </Slider>
            ) : (
              <Spin />
            )}
          </>
        )}
      </>
    );
  }
}

function mapStateToProps(state: any) {
  return {
    monthFilters: state.orbita.Financial.loadFilterByMonth,
    filterExtract: state.components.ExtractFilter.extractFilter,
  };
}

export default connect(mapStateToProps)(styles(Filter));
